/**
 * Client-side task-completion gate (#2077).
 *
 * When a project marks Photos and/or Documents required, the rep must attach at
 * least one of each before finishing the task. This mirrors the existing
 * required-survey gate ("N required question(s) remaining") and runs entirely
 * client-side (offline-friendly; no server rejection — decision on #2077).
 *
 * Pure + dependency-free so the Finish handler can call it and it can be
 * unit-tested in isolation.
 */

export interface TaskCompletionFlags {
  requiresPhotos: boolean;
  requiresDocuments: boolean;
}

export interface TaskCompletionCounts {
  photoCount: number;
  documentCount: number;
  /** Required survey questions still unanswered (0 when none / not applicable). */
  surveyRemaining: number;
}

export interface TaskCompletionResult {
  allowed: boolean;
  /** Human-readable reasons the task can't finish yet, in display order. */
  missing: string[];
}

/**
 * Decide whether the active task may be finished. Order of `missing` is
 * survey → photos → documents so the message reads consistently.
 */
export function evaluateTaskCompletion(
  flags: TaskCompletionFlags,
  counts: TaskCompletionCounts
): TaskCompletionResult {
  const missing: string[] = [];
  if (counts.surveyRemaining > 0) {
    missing.push(`${counts.surveyRemaining} required survey question(s) remaining`);
  }
  if (flags.requiresPhotos && counts.photoCount < 1) {
    missing.push('Photo required');
  }
  if (flags.requiresDocuments && counts.documentCount < 1) {
    missing.push('Document required');
  }
  return { allowed: missing.length === 0, missing };
}
